import { useCallback, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Checkbox, Modal, InputField, Select, Button } from '@ets/design-system';

import { useDispatch, useSelector } from '_common/hooks/redux';
import { InstanceService } from '_common/services';
import { toast } from 'utils/toast';
import { useObject } from '_common/hooks';

import { closeAndResetModal, openAndUpdateModal } from '_common/modals/ModalsSlice';
import { selectOrderedElementStatusList } from '_common/services/api/elementStatusApi';
import { useChangeElementStatusMutation } from 'App/redux/objectApi';

import { Textarea, type TextareaProps } from '_common/lib/components';

const MESSAGES = {
  header: 'CHANGE_STATUS',
  statusLabel: 'STATUS',
  commentLabel: 'STATUS_CHANGE_COMMENT',
  commentPlaceholder: 'storage.modals.approve.placeholder',
  checkbox: 'CHANGE_STATUS_ON_CHILDREN',
  cancel: 'global.cancel',
  required: 'REQUIRED',
  confirmationLabel: 'CONFIRM_CHANGE_STATUS_LABEL',
  confirmationPlaceholder: 'CONFIRM_CHANGE_STATUS_PLACEHOLDER',
};

const ChangeElementStatusModal = () => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const isOpen = useSelector((state) => state.modals.open.ChangeElementStatusModal);
  const list = useSelector(selectOrderedElementStatusList);
  const objectId = useSelector((state) => state.modals.ChangeElementStatusModal.objectId);
  const objectType = useSelector((state) => state.modals.ChangeElementStatusModal.objectType);
  const { data: object } = useObject(
    { object_id: objectId, object_type: objectType },
    { skip: !objectId && !objectType },
  );
  const submitting = useSelector((state) => state.modals.ChangeElementStatusModal.submitting);
  const actionCode = useSelector((state) => state.modals.ConfirmationModal.actionCode);

  const [canRender, setCanRender] = useState(false);
  const [status, setStatus] = useState<{ value: string; label: string }>({ value: '', label: '' });
  const [comment, setComment] = useState('');
  const [commentError, setCommentError] = useState<string>('');
  const [recursive, setRecursive] = useState(false);
  const [recursiveDisabled, setRecursiveDisabled] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [changeElementStatus] = useChangeElementStatusMutation();
  const newStatus = list.find((s) => s.id === status?.value);
  const oldStatus = list.find((s) => s.id === object?.status);

  const close = useCallback(() => {
    dispatch(closeAndResetModal('ChangeElementStatusModal'));
  }, [dispatch]);

  useEffect(() => {
    return () => {
      setStatus({ value: '', label: '' });
      setComment('');
      setCommentError('');
    };
  }, [isOpen]);

  useEffect(() => {
    if (object?.status) {
      const index = list.findIndex((status) => status.id === object.status);
      if (index > -1 && list.length - 1 !== index) {
        setStatus({ value: list[index + 1].id, label: list[index + 1].name });
      }
    }

    const changeAllowed = async () => {
      const { data } = await new InstanceService().changeElementStatusAllowed({
        id: object?.id,
        type: object?.type,
      });
      if (!data.allowed) {
        toast({
          variant: 'error',
          title: 'STATUS_CANNOT_BE_UPDATED',
          message: 'STATUS_CANNOT_BE_UPDATED_MESSAGE',
          messageValues: { elementType: intl.formatMessage({ id: `global.${object?.type}` }) },
        });
        close();
      } else {
        setCanRender(true);
      }
    };
    if (object) {
      changeAllowed();
    }
  }, [close, intl, list, object]);

  useEffect(() => {
    if (status) {
      if (oldStatus?.confirm_input || newStatus?.confirm_input) {
        setShowConfirmation(true);
      } else {
        setShowConfirmation(false);
      }
      if (newStatus?.allow_edit) {
        setRecursiveDisabled(false);
      } else {
        setRecursive(true);
        setRecursiveDisabled(true);
      }
    }
  }, [newStatus?.allow_edit, newStatus?.confirm_input, oldStatus?.confirm_input, status]);

  useEffect(() => {
    if (actionCode) {
      setCanRender(false);
    } else {
      setCanRender(true);
    }
  }, [actionCode]);

  const handleCommentChange: TextareaProps['onChange'] = (e) => {
    const target = e.target as HTMLTextAreaElement;
    const value = target.value;

    if (value.length > 200) {
      setCommentError(intl.formatMessage({ id: 'ERROR_CHARACTER_LIMIT' }, { number: 200 }));
    } else {
      setCommentError('');
    }
    setComment(value);
  };

  const submit = async () => {
    const { data } = await new InstanceService().changeElementStatusAllowed({
      id: object?.id,
      type: object?.type,
    });

    if (!data.allowed) {
      toast({
        variant: 'error',
        title: 'STATUS_CANNOT_BE_UPDATED',
        message: 'STATUS_CANNOT_BE_UPDATED_MESSAGE',
        messageValues: { elementType: intl.formatMessage({ id: `global.${object?.type}` }) },
      });
      close();
      return;
    }

    const payload = {
      objectId: object?.id ?? objectId,
      objectType: object?.type ?? objectType,
      status: status.value,
      comment,
      recursive,
    };

    const messageValues = {
      elementName: object?.name ?? '',
      oldStatus: oldStatus?.name ?? '',
      newStatus: newStatus?.name ?? '',
    };

    if (showConfirmation) {
      dispatch(
        openAndUpdateModal({
          modal: 'ConfirmationModal',
          data: {
            title: 'CONFIRM_STATUS_UPDATE',
            message: `ARE_YOU_SURE_YOU_WANT_TO_UPDATE_THE_STATUS`,
            messageValues,
            confirmButtonTextId: 'UPDATE_STATUS',
            confirmButtonType: 'primary',
            cancelButtonTextId: 'global.cancel',
            cancelButtonShow: true,
            actionCode: 'changeElementStatus',
            actionValue: { payload, messageValues },
            width: '60rem',
          },
        }),
      );
    } else {
      changeElementStatus({ payload, messageValues }).then(() => {
        close();
      });
    }
  };
  const isSubmitDisabled = () => {
    // No status selected
    if (status === null) {
      return true;
    }
    // Changing to approved status requires a comment
    if (status?.value === 'approved' && comment === '') {
      return true;
    }
    // Current comment has an error
    if (commentError !== '') {
      return true;
    }
    if (submitting) {
      return true;
    }

    return false;
  };

  if (!canRender) {
    return null;
  }

  return (
    <Modal open={!!isOpen} width="60rem" close={close} testId="change-status">
      <Modal.Header>
        <FormattedMessage id={MESSAGES.header} />
      </Modal.Header>
      <Modal.Body style={{ scrollbarGutter: 'auto' }}>
        <InputField
          style={{ width: '100%' }}
          size="l"
          label={intl.formatMessage({ id: MESSAGES.statusLabel })}
          testId="change-status-list"
        >
          <Select
            variant="standard"
            isClearable={false}
            width="100%"
            size="l"
            options={list
              .filter((status) => status.allow_change)
              .map((status) => ({
                value: status.id,
                label: status.name,
              }))}
            value={status.value ? status : null}
            onChange={(newValue) => {
              if (newValue) {
                setStatus(newValue);
              }
            }}
            filterOption={({ value }: { value: string }) => value !== object?.status}
            testId="change-status-list"
            menuPosition="fixed"
          />
        </InputField>
        <InputField
          size="l"
          label={intl.formatMessage({ id: MESSAGES.commentLabel })}
          feedbackContent={commentError}
          extra={status?.value === 'approved' ? intl.formatMessage({ id: MESSAGES.required }) : ''}
          testId="change-status-comment"
        >
          <Textarea
            plainText
            size="m"
            name="status-comment"
            type="text"
            placeholder={intl.formatMessage({ id: MESSAGES.commentPlaceholder })}
            initialContent={comment}
            onChange={handleCommentChange}
            hasError={!!commentError}
            testId="change-status-comment-textarea"
          />
        </InputField>

        {object?.type === 'folder' && (
          <Checkbox
            size="s"
            checked={recursive}
            onChange={() => setRecursive((value) => !value)}
            disabled={recursiveDisabled}
            testId="change-status-on-children"
          >
            <FormattedMessage id={MESSAGES.checkbox} />
          </Checkbox>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button size="m" variant="standard" onClick={close} testId="change-status-cancel">
          <FormattedMessage id={MESSAGES.cancel} />
        </Button>
        <Button
          size="m"
          variant="primary"
          disabled={isSubmitDisabled()}
          onClick={submit}
          loading={submitting}
          style={{ margin: '0 0 0 1rem' }}
          testId="change-status-submit"
        >
          <FormattedMessage id={MESSAGES.header} />
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ChangeElementStatusModal;
